import { useMemo, useState } from 'react';
import { CalendarPlus, Edit, Plus, Power } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { useToast } from '../../../shared/components/Toast';
import { FEATURES } from '../../../shared/lib/permissions';
import { useEstados, useMunicipios } from '../../geo/hooks';
import { ActiveFilter, Holiday, HolidayScope, mensagemDeErro } from '../api';
import { FeriadoFormModal } from '../components/FeriadoFormModal';
import { SituacaoBadge } from '../components/SituacaoBadge';
import { SCOPE_LABEL, TYPE_LABEL, diaDaSemana, formatarDataCivil } from '../components/format';
import { useFeriados, useGerarFeriados, useSituacaoFeriado } from '../hooks';

const TODOS = '';

export default function FeriadosPage() {
  const toast = useToast();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [scope, setScope] = useState<HolidayScope | typeof TODOS>(TODOS);
  const [uf, setUf] = useState(TODOS);
  const [cityId, setCityId] = useState(TODOS);
  const [active, setActive] = useState<ActiveFilter>('true');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Holiday | null>(null);
  const [toggling, setToggling] = useState<Holiday | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);

  const estados = useEstados();
  const municipios = useMunicipios(uf || null);
  const feriados = useFeriados({
    year,
    scope: scope || undefined,
    stateUf: uf || undefined,
    cityId: cityId ? Number(cityId) : undefined,
    active,
  });
  const situacao = useSituacaoFeriado();
  const gerar = useGerarFeriados();
  const items = feriados.data ?? [];

  const yearOptions = useMemo(
    () => Array.from({ length: 6 }, (_, i) => currentYear - 1 + i).map((y) => ({ value: String(y), label: String(y) })),
    [currentYear],
  );

  function openForm(feriado: Holiday | null) {
    setEditing(feriado);
    setFormOpen(true);
  }

  async function confirmToggle() {
    if (!toggling) return;
    try {
      await situacao.mutateAsync({ id: toggling.id, active: !toggling.active });
      toast.success(toggling.active ? `"${toggling.name}" inativado.` : `"${toggling.name}" reativado.`);
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível alterar a situação do feriado.'));
    } finally {
      setToggling(null);
    }
  }

  async function confirmGenerate() {
    try {
      const result = await gerar.mutateAsync(year);
      toast.success(`${result.created} feriado(s) criado(s); ${result.skipped} já existia(m).`, {
        title: `Feriados de ${year}`,
      });
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível gerar os feriados.'));
    } finally {
      setGenerateOpen(false);
    }
  }

  function localidade(h: Holiday) {
    if (h.scope === 'nacional') return 'Brasil';
    if (h.scope === 'estadual') return h.stateUf ?? '—';
    return `${h.cityName ?? h.cityId}/${h.stateUf}`;
  }

  return (
    <PageContainer
      title="Feriados"
      subtitle="Feriados e pontos facultativos usados no cálculo de férias e no calendário de trabalho."
      breadcrumbs={[{ label: 'Cadastros' }, { label: 'Feriados' }]}
      action={
        <Can feature={FEATURES.masterDataFeriados} action="create">
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setGenerateOpen(true)}>
              <CalendarPlus className="mr-2 h-5 w-5" />
              Gerar feriados do ano
            </Button>
            <Button onClick={() => openForm(null)}>
              <Plus className="mr-2 h-5 w-5" />
              Novo feriado
            </Button>
          </div>
        </Can>
      }
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[140px]">
          <AutocompleteSelect label="Ano" options={yearOptions} value={String(year)} onChange={(v) => setYear(Number(v))} />
        </div>
        <div className="min-w-[180px]">
          <AutocompleteSelect
            label="Abrangência"
            options={[
              { value: TODOS, label: 'Todas' },
              ...(Object.keys(SCOPE_LABEL) as HolidayScope[]).map((s) => ({ value: s, label: SCOPE_LABEL[s] })),
            ]}
            value={scope}
            onChange={(v) => setScope(v as HolidayScope | typeof TODOS)}
          />
        </div>
        <div className="min-w-[200px]">
          <AutocompleteSelect
            label="Estado"
            options={[
              { value: TODOS, label: 'Todos' },
              ...(estados.data ?? []).map((e) => ({ value: e.uf, label: `${e.nome} (${e.uf})` })),
            ]}
            value={uf}
            onChange={(v) => {
              setUf(v);
              setCityId(TODOS);
            }}
          />
        </div>
        <div className="min-w-[220px]">
          <AutocompleteSelect
            label="Município"
            options={[
              { value: TODOS, label: 'Todos' },
              ...(municipios.data ?? []).map((m) => ({ value: String(m.idIbge), label: m.nome })),
            ]}
            value={cityId}
            onChange={setCityId}
            disabled={!uf}
          />
        </div>
        <div className="min-w-[160px]">
          <AutocompleteSelect
            label="Situação"
            options={[
              { value: 'true', label: 'Ativos' },
              { value: 'false', label: 'Inativos' },
              { value: 'all', label: 'Todos' },
            ]}
            value={active}
            onChange={(v) => setActive(v as ActiveFilter)}
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        {feriados.isLoading ? (
          <p className="p-6 text-sm text-gray-500">Carregando feriados…</p>
        ) : feriados.isError ? (
          <p className="p-6 text-sm text-red-600">{mensagemDeErro(feriados.error, 'Não foi possível carregar os feriados.')}</p>
        ) : items.length === 0 ? (
          <EmptyState
            title={`Nenhum feriado em ${year}`}
            description="Use “Gerar feriados do ano” para criar os nacionais, ou cadastre um feriado estadual ou municipal."
          />
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Abrangência</th>
                <th className="px-4 py-3">Localidade</th>
                <th className="px-4 py-3">Situação</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((h) => (
                <tr key={h.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-3 text-gray-900">
                    {formatarDataCivil(h.date)}
                    <p className="text-xs text-gray-500">{diaDaSemana(h.date)}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-900">
                    {h.name}
                    {h.recurring && <p className="text-xs text-gray-500">Repete todo ano</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{TYPE_LABEL[h.type]}</td>
                  <td className="px-4 py-3 text-gray-600">{SCOPE_LABEL[h.scope]}</td>
                  <td className="px-4 py-3 text-gray-600">{localidade(h)}</td>
                  <td className="px-4 py-3"><SituacaoBadge active={h.active} /></td>
                  <td className="px-4 py-3 text-right">
                    <Can feature={FEATURES.masterDataFeriados} action="edit">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openForm(h)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                          aria-label={`Editar ${h.name}`}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setToggling(h)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600"
                          aria-label={h.active ? `Inativar ${h.name}` : `Reativar ${h.name}`}
                        >
                          <Power className="h-4 w-4" />
                        </button>
                      </div>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <FeriadoFormModal
        open={formOpen}
        feriado={editing}
        onClose={() => setFormOpen(false)}
        onSaved={(message) => {
          setFormOpen(false);
          toast.success(message);
        }}
      />

      <ConfirmDialog
        open={generateOpen}
        title={`Gerar feriados de ${year}?`}
        message={`Serão criados os feriados nacionais de ${year} (fixos e móveis) e repetidos os estaduais e municipais marcados como recorrentes em ${year - 1}. Os que já existirem serão mantidos.`}
        confirmLabel="Gerar"
        busy={gerar.isPending}
        onConfirm={confirmGenerate}
        onCancel={() => setGenerateOpen(false)}
      />

      <ConfirmDialog
        open={toggling !== null}
        title={toggling?.active ? 'Inativar feriado?' : 'Reativar feriado?'}
        message={
          toggling?.active
            ? `"${toggling?.name}" deixará de ser considerado no cálculo de férias.`
            : `"${toggling?.name}" voltará a ser considerado.`
        }
        confirmLabel={toggling?.active ? 'Inativar' : 'Reativar'}
        danger={toggling?.active}
        busy={situacao.isPending}
        onConfirm={confirmToggle}
        onCancel={() => setToggling(null)}
      />
    </PageContainer>
  );
}
